import * as React from 'react';
import classNames from 'classnames';
import Label from 'box-ui-elements/es/components/label/Label';
import TextArea from 'box-ui-elements/es/components/text-area/TextArea';
import TextInput from 'box-ui-elements/es/components/text-input/TextInput';
import Tooltip from 'box-ui-elements/es/components/tooltip/Tooltip';
import { isValidValue } from 'box-ui-elements/es/features/metadata-instance-fields/validateMetadataField';
import { FIELD_TYPE_FLOAT } from 'box-ui-elements/es/features/metadata-instance-fields/constants';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import messages from './messages';
import {
  TEXTTYPE_TEXT,
  TEXTTYPE_EMAIL,
  TEXTTYPE_NUMBER,
} from '../../../constants';
import css from './buie-text-field.module.scss';
import useId from '../../utils/useId';

import type { Props } from './text-field';

type BuieTextFieldProps = Props & {
  intl: IntlShape;
};

const BuieTextField = React.memo<BuieTextFieldProps>(
  (props: BuieTextFieldProps) => {
    const {
      className = '',
      label,
      description,
      error,
      type = TEXTTYPE_TEXT,
      infoText,
      isRequired,
      isDisabled,
      isMultiLine,
      limit = null,
      onChange,
      onBlur,
      value,
      isInTwoColumn,
      intl,
    } = props;

    const [isHovering, setIsHovering] = React.useState(false);

    const mouseProps = {
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false),
    } as const;

    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const handleOnWheel = (event: any) => {
      // call props.onWheel(), allows for jest mock functions to be passed in for testing.
      if (props.onWheel) {
        props.onWheel();
      }

      // Prevent the input value change
      event.target.blur();
      // Refocus immediately, on the next tick (after the current function is done)
      setTimeout(() => {
        event.target.focus();
      }, 0);
    };

    const numberInputProps =
      type === TEXTTYPE_NUMBER ? { onWheel: handleOnWheel } : {};
    const isTooltipShown = !!infoText && isHovering;

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // If this is a number field, we use BUIE validation for metadata float type
      // All other types will pass through as valid
      const metadataType = type === TEXTTYPE_NUMBER ? FIELD_TYPE_FLOAT : type;
      if (isValidValue(metadataType, newValue)) {
        onChange(newValue);
      }
    };

    const classes = classNames('text-field-container', className, {
      [css.withTooltip]: isTooltipShown,
      [css.withoutTooltip]: !isTooltipShown,
      [css.halfColumnWidth]: isInTwoColumn,
    });

    let placeholder = '';
    if (isMultiLine) {
      placeholder = intl.formatMessage(
        messages.multilineTextFieldPlaceholderMessage,
      );
    } else if (type === TEXTTYPE_EMAIL) {
      placeholder = intl.formatMessage(
        messages.emailTextFieldPlaceholderMessage,
      );
    } else if (type === TEXTTYPE_NUMBER) {
      placeholder = intl.formatMessage(
        messages.numberTextFieldPlaceholderMessage,
      );
    } else {
      placeholder = intl.formatMessage(messages.textFieldPlaceholderMessage);
    }

    const hasError = !!error;

    const errorMessageID = useId('errorMessage');
    const descriptionID = useId('description');
    const ariaAttrs = {
      'aria-invalid': hasError,
      'aria-required': isRequired,
      'aria-describedby': description ? descriptionID : undefined,
      'aria-errormessage': errorMessageID,
    } as const;

    const inputId = useId('input-');

    return (
      <div className={classes}>
        <Label labelElProps={{ htmlFor: inputId }} text={label} />

        {!!description && (
          <div className="text-field-description" id={descriptionID}>
            {description}
          </div>
        )}

        <Tooltip
          className="field-tooltip"
          isShown={isTooltipShown}
          position="middle-right"
          text={infoText}
        >
          {isMultiLine ? (
            <TextArea
              className={css.textField}
              data-testid="buie-textarea"
              disabled={isDisabled}
              error={error}
              errorTooltipPosition="top-left"
              hideOptionalLabel
              id={inputId}
              isRequired={isRequired}
              maxLength={limit}
              onChange={handleOnChange}
              onBlur={onBlur}
              placeholder={placeholder}
              value={value}
              {...mouseProps}
              {...ariaAttrs}
            />
          ) : (
            <TextInput
              className={css.textField}
              data-testid="buie-textinput"
              disabled={isDisabled}
              error={error}
              errorPosition="top-left"
              hideOptionalLabel
              id={inputId}
              isRequired={isRequired}
              maxLength={limit}
              onChange={handleOnChange}
              onBlur={onBlur}
              placeholder={placeholder}
              type={type}
              value={value}
              {...mouseProps}
              {...ariaAttrs}
              {...numberInputProps}
            />
          )}
        </Tooltip>
      </div>
    );
  },
);

export default injectIntl(BuieTextField);
